import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Plus,
  Trash2,
  Edit2,
  Eye,
  Search,
  PhoneCall,
  Download,
  Filter,
} from "lucide-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import Modal from "../../../components/modal/Modal";
import CreateExternalAgency from "./CreateExternalAgency";
import ViewExternalAgency from "./ViewExternalAgency";
import { useExternalAgenciesStore } from "../../../store/store";
import { ExternalAgency } from "../../../types/api";

const ExternalAgenciesCoordination: React.FC = () => {
  const { agencies, loading, fetchAgencies, deleteAgency, searchAgencies, exportAgencies } = useExternalAgenciesStore();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<ExternalAgency | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    agencyName: string;
    id: string;
  }>({ open: false, agencyName: "", id: "" });

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);


  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm) {
        searchAgencies(searchTerm);
      } else {
        fetchAgencies();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchAgencies, fetchAgencies]);

  // Computed Statistics
  const stats = useMemo(() => {
    return {
      total: agencies.length,
      active: agencies.filter((a) => a.status === "Active").length,
      hospitals: agencies.filter((a) => a.category === "Hospital").length,
      emergency: agencies.filter((a) =>
        ["Fire Brigade", "Police", "Ambulance Service"].includes(a.category)
      ).length,
    };
  }, [agencies]);

  const filteredData = useMemo(() => {
    if (selectedCategory === "All") return agencies;
    return agencies.filter((agency) => agency.category === selectedCategory);
  }, [agencies, selectedCategory]);

  const handleCreate = () => {
    setSelectedAgency(null);
    setIsEditMode(false);
    setCreateModalOpen(true);
  };

  const handleEdit = (agency: ExternalAgency) => {
    setSelectedAgency(agency);
    setIsEditMode(true);
    setCreateModalOpen(true);
  };

  const handleView = (agency: ExternalAgency) => {
    setSelectedAgency(agency);
    setViewModalOpen(true);
  };

  const confirmDelete = (agencyName: string, id: string) => {
    setDeleteConfirmation({ open: true, agencyName, id });
  };

  const handleDelete = async () => {
    if (deleteConfirmation.id) {
      await deleteAgency(deleteConfirmation.id);
      setDeleteConfirmation({ open: false, agencyName: "", id: "" });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ open: false, agencyName: "", id: "" });
  };

  const handleSave = () => {
    setCreateModalOpen(false);
    fetchAgencies(); // Refresh data
  };

  const handleQuickCall = (number: string) => {
    window.location.href = `tel:${number}`;
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Agency Name",
      field: "agencyName",
      flex: 1.5,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams) => (
        <span className="font-medium text-gray-800">{params.value}</span>
      ),
    },
    {
      headerName: "Category",
      field: "category",
      flex: 1,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams) => (
        <span className="px-2 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600 border border-gray-200">
          {params.value}
        </span>
      ),
    },
    {
      headerName: "Contact Person",
      field: "contactPerson",
      flex: 1.2,
      sortable: true,
    },
    {
      headerName: "Contact Number",
      field: "contactNumber",
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => (
        <div className="flex items-center gap-2 group">
          <span>{params.value}</span>
          <button
            onClick={() => handleQuickCall(params.value)}
            className="p-1 rounded-full bg-green-100 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-green-200"
            title="Quick Call"
          >
            <PhoneCall size={14} />
          </button>
        </div>
      ),
    },
    {
      headerName: "Status",
      field: "status",
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${params.value === "Active"
              ? "bg-[#0caba8] text-white"
              : "bg-gray-200 text-gray-600"
            }`}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: "MOU Expiry Date",
      field: "mouContractExpiryDate",
      flex: 1,
      sortable: true,
      cellRenderer: (params: ICellRendererParams) => (
        <span className="text-gray-600">
          {params.value ? new Date(params.value).toLocaleDateString() : "-"}
        </span>
      )
    },
    {
      headerName: "Actions",
      field: "_id", // Use _id from backend
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => (
        <div className="flex gap-2 items-center h-full">
          <button
            onClick={() => handleView(params.data)}
            className="p-1.5 hover:bg-teal-50 text-teal-600 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye size={18} />
          </button>
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => confirmDelete(params.data.agencyName, params.value)}
            className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  if (loading && agencies.length === 0) {
    return <div className="p-10 text-center">Loading agencies...</div>;
  }

  return (
    <div className="px-2 py-4 mt-14 w-full h-full bg-gray-50/50 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl text-gray-900">
            Coordination with External Agencies
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportAgencies}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm font-medium text-sm"
          >
            <Download size={18} />
            Export Directory
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-[#0fa9a6] text-white px-5 py-2 rounded-full hover:bg-teal-600 transition-all font-semibold shadow-md focus:ring-4 focus:ring-teal-100"
          >
            <Plus size={20} />
            Add Agency
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm font-medium">Total Agencies</p>
            <h3 className="text-2xl font-bold text-gray-800">{stats.total}</h3>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <Building size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm font-medium">Active Partners</p>
            <h3 className="text-2xl font-bold text-gray-800">{stats.active}</h3>
          </div>
          <div className="p-3 bg-green-50 text-green-600 rounded-lg">
            <Activity size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm font-medium">Hospitals</p>
            <h3 className="text-2xl font-bold text-gray-800">{stats.hospitals}</h3>
          </div>
          <div className="p-3 bg-red-50 text-red-600 rounded-lg">
            <Activity size={24} />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm font-medium">Emergency Services</p>
            <h3 className="text-2xl font-bold text-gray-800">{stats.emergency}</h3>
          </div>
          <div className="p-3 bg-orange-50 text-orange-600 rounded-lg">
            <PhoneCall size={24} />
          </div>
        </div>
      </div>

      {/* Filters & Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/30">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search agencies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="hidden sm:block h-6 w-px bg-gray-300"></div>

            <div className="flex items-center gap-2">
              <Filter size={18} className="text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border-none bg-transparent text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer hover:bg-gray-100 p-1 rounded"
              >
                <option value="All">All Categories</option>
                <option value="Hospital">Hospital</option>
                <option value="Fire Brigade">Fire Brigade</option>
                <option value="Police">Police</option>
                <option value="Ambulance Service">Ambulance Service</option>
                <option value="Disaster Management">Disaster Management</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="text-sm text-gray-500 font-medium">
            Showing {filteredData.length} records
          </div>
        </div>

        {/* Table */}
        <div className="ag-theme-alpine w-full h-[600px]">
          <AgGridReact
            rowData={filteredData}
            columnDefs={columnDefs}
            rowHeight={60}
            headerHeight={65}
            pagination={true}
            paginationPageSize={10}
            animateRows={true}
            suppressCellFocus={true}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
          />
        </div>
      </div>

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        closeButton={false}
        modalClass="bg-transparent flex items-center justify-center p-4 w-full max-w-4xl"
      >
        <CreateExternalAgency
          onClose={() => setCreateModalOpen(false)}
          onSave={handleSave}
          agencyToEdit={selectedAgency}
          isEdit={isEditMode}
        />
      </Modal>

      {/* Modal for View */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setViewModalOpen(false)}
        closeButton={false}
        modalClass="bg-transparent flex items-center justify-center p-4 w-full max-w-4xl"
      >
        {selectedAgency && (
          <ViewExternalAgency
            onClose={() => setViewModalOpen(false)}
            agency={selectedAgency}
          />
        )}
      </Modal>

      {/* Modal for Delete Confirmation */}
      <Modal
        isOpen={deleteConfirmation.open}
        onClose={cancelDelete}
        closeButton={false}
        modalClass="bg-white rounded-xl shadow-lg w-full max-w-md mx-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-center mb-6 text-red-500">
            <div className="p-3 bg-red-100 rounded-full">
              <Trash2 size={32} />
            </div>
          </div>
          <h3 className="text-center text-lg font-bold text-gray-900 mb-2">
            Delete Agency?
          </h3>
          <p className="text-center text-gray-500 mb-8">
            Are you sure you want to delete <span className="font-semibold text-gray-800">{deleteConfirmation.agencyName}</span>? This action cannot be undone.
          </p>
          <div className="flex gap-4">
            <button
              onClick={cancelDelete}
              className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Delete System
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Icons for stats
const Building = ({ size, className }: { size: number, className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
    <line x1="9" y1="22" x2="9" y2="22.01"></line>
    <line x1="15" y1="22" x2="15" y2="22.01"></line>
    <line x1="12" y1="22" x2="12" y2="22.01"></line>
    <line x1="12" y1="2" x2="12" y2="22"></line>
    <line x1="4" y1="10" x2="20" y2="10"></line>
    <line x1="4" y1="18" x2="20" y2="18"></line>
  </svg>
);

const Activity = ({ size, className }: { size: number, className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
);

export default ExternalAgenciesCoordination;
